import api from "@/services/api"
import type { RecursivelyConvertDatesToStrings } from "@/utils/typeUtilities"
import type { MaybeRef } from "@vueuse/core"
import { useQuery } from "@tanstack/vue-query"
import { BASE_TRANSACTION_REPORT_QUERY_KEY } from "@/services/queryKeys"

export const transactionReportNamedPeriods = [
  "Today",
  "Yesterday",
  "Last 7 days",
  "Last 28 days",
] as const

export type TransactionReportPeriod =
  | { date: Date }
  | (typeof transactionReportNamedPeriods)[number]

type TransactionReportApiQuery = {
  from: Date
  until: Date
  granularity: "hour" | "day" | "week" | "month"
}

type TransactionReport = Array<{
  start: Date
  end: Date
  cash: number
  card: number
  total: number
}>

export const retrieveTransactionReport = async (
  period: TransactionReportPeriod
): Promise<TransactionReport> => {
  let apiQuery: TransactionReportApiQuery | undefined
  switch (period) {
    case "Today": {
      apiQuery = {
        from: DateFns.startOfToday(),
        until: DateFns.endOfToday(),
        granularity: "hour",
      }
      break
    }
    case "Yesterday": {
      apiQuery = {
        from: DateFns.startOfYesterday(),
        until: DateFns.endOfYesterday(),
        granularity: "hour",
      }
      break
    }
    case "Last 7 days": {
      apiQuery = {
        from: DateFns.addWeeks(DateFns.startOfToday(), -1),
        until: DateFns.endOfYesterday(),
        granularity: "day",
      }
      break
    }
    case "Last 28 days": {
      apiQuery = {
        from: DateFns.addWeeks(DateFns.startOfToday(), -4),
        until: DateFns.endOfYesterday(),
        granularity: "day",
      }
      break
    }
    default:
      apiQuery = {
        from: DateFns.startOfDay(period.date),
        until: DateFns.endOfDay(period.date),
        granularity: "hour",
      }
  }

  const { body } = await api.get<
    RecursivelyConvertDatesToStrings<TransactionReport>
  >("/reports/transactions", {
    query: apiQuery,
  })

  return body.map((entry) => ({
    ...entry,
    start: DateFns.parseISO(entry.start),
    end: DateFns.parseISO(entry.end),
  }))
}

export const useTransactionReport = (
  query: MaybeRef<TransactionReportPeriod>
) =>
  useQuery(
    [BASE_TRANSACTION_REPORT_QUERY_KEY, query],
    ({ queryKey: [_name, query_] }) =>
      retrieveTransactionReport(
        unref(query_ as MaybeRef<TransactionReportPeriod>)
      ),
    { refetchInterval: 60 * 1000 }
  )
